/**
 * The organization identity card — WHICH tenant this admin shell is in.
 *
 * Rendered ONCE, under the Vantio wordmark. It used to be rendered twice, the
 * second copy sitting above the session controls, which said the same thing in
 * the same words a screen apart and pushed the user's own account further down
 * for nothing. The footer now carries the SESSION (who you are); this card
 * carries the ORGANIZATION (where you are). One question each.
 *
 * The logo comes from `Organization.sidebar_icon_url` via
 * `user.organization_sidebar_icon_url` — NOT from the billing logo. Those were
 * the same value until this component's field changed: the billing one is the
 * invoice letterhead mark, so a sidebar icon could not be chosen without
 * restyling every invoice the organization sends.
 *
 * NOTHING HERE IS TENANT-SPECIFIC. No organization id, name or logo is hardcoded
 * and there is no id → logo map: the values arrive with the session and any org
 * renders correctly, including one that has configured nothing. The identity
 * fields reached `UserMe` precisely so the sidebar would not have to call the
 * admin-only billing-settings endpoint (managers reach this shell too), so this
 * component issues NO request of its own.
 *
 * THREE-STEP LOGO FALLBACK, because two of the three states are normal rather
 * than exceptional:
 *
 *   1. a usable `logoUrl`            → the image
 *   2. no logo configured (null/"")  → monogram from the name
 *   3. the image 404s at runtime     → monogram, via onError
 *
 * A monogram needs a name; when the org has neither, a neutral building glyph
 * stands in rather than initials derived from a UUID, which would read as
 * meaningless noise in the one place the user looks to confirm where they are.
 */
import React from "react";
import { Building2, Check, Copy } from "lucide-react";

/** Up to two initials from the org name, or "" when there is nothing to derive. */
export function organizationInitials(name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/** Is this logo value worth putting in an <img src>? */
export function isUsableLogoUrl(value) {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * The square logo tile, with its own error state.
 *
 * `errored` is component state rather than a prop so a broken URL degrades once,
 * on the render that discovers it, without the parent needing to know. Resetting
 * on `logoUrl` change matters for impersonation: switching orgs must re-try the
 * new logo instead of inheriting the previous org's failure.
 */
function OrganizationAvatar({ theme, name, logoUrl, size }) {
  const [errored, setErrored] = React.useState(false);
  React.useEffect(() => {
    setErrored(false);
  }, [logoUrl]);

  const initials = organizationInitials(name);
  const showImage = isUsableLogoUrl(logoUrl) && !errored;

  const box =
    theme === "light"
      ? "flex shrink-0 items-center justify-center overflow-hidden rounded-[9px] border border-zinc-200 bg-white"
      : "flex shrink-0 items-center justify-center overflow-hidden rounded-[9px] border border-white/[0.08] bg-white/[0.05]";
  const monogramText =
    theme === "light" ? "font-bold text-violet-700" : "font-bold text-violet-200";

  return (
    <span className={`${box} ${size.box}`} data-org-avatar>
      {showImage ? (
        <img
          src={logoUrl}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-contain"
          onError={() => setErrored(true)}
          data-org-logo-img
        />
      ) : initials ? (
        <span className={`${monogramText} ${size.text}`} aria-hidden="true" data-org-monogram>
          {initials}
        </span>
      ) : (
        <Building2
          className={`${size.icon} ${theme === "light" ? "text-zinc-400" : "text-slate-500"}`}
          strokeWidth={1.75}
          aria-hidden="true"
          data-org-monogram
        />
      )}
    </span>
  );
}

/**
 * One size, because there is one card.
 *
 * This was a map keyed by `variant`, with a smaller footer entry. That variant
 * described a card that no longer exists, so the map and the prop that selected
 * it went with it rather than being left behind as a choice with one option.
 */
const AVATAR = { box: "h-9 w-9", text: "text-[12px]", icon: "h-4 w-4" };

/**
 * @param {object}  props
 * @param {"light"|"dark"} props.theme
 * @param {string|null}    props.name             `user.organization_name`
 * @param {string|null}    props.logoUrl          `user.organization_sidebar_icon_url`
 * @param {string}         props.organizationId   `user.organization_id`
 * @param {boolean}        props.showCopy         render the copy-id control
 */
export default function OrganizationIdentity({
  theme,
  name,
  logoUrl,
  organizationId,
  showCopy = false,
}) {
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  const id = String(organizationId || "");
  // An org with no configured name still has to render as SOMETHING. "Organisation"
  // is honest — it does not invent a name and it does not leave a blank row.
  const displayName = String(name || "").trim() || "Organisation";

  const handleCopy = async () => {
    // Wrapped end to end: clipboard access is permission-gated and simply absent
    // over plain HTTP and in some embedded webviews. A sidebar must not break
    // because a convenience button could not reach the clipboard.
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      /* no clipboard: the id stays visible and selectable in the row above */
    }
  };

  const card =
    theme === "light"
      ? "flex items-center gap-2.5 rounded-[11px] border border-zinc-200 bg-zinc-50/80 px-2.5 py-2"
      : "flex items-center gap-2.5 rounded-[11px] border border-white/[0.07] bg-white/[0.03] px-2.5 py-2";
  const nameClass =
    theme === "light"
      ? "truncate text-[12px] font-semibold text-zinc-900"
      : "truncate text-[12px] font-semibold text-[#f1f5f9]";
  const idClass =
    theme === "light"
      ? "truncate font-mono text-[9.5px] text-zinc-500"
      : "truncate font-mono text-[9.5px] text-[#8b9ab5]";
  const copyBtn =
    theme === "light"
      ? "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] border border-zinc-200 bg-white text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60"
      : "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] border border-white/[0.09] bg-white/[0.04] text-[#8b9ab5] transition-colors hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70";

  return (
    <div className={card} data-org-identity>
      <OrganizationAvatar theme={theme} name={name} logoUrl={logoUrl} size={AVATAR} />

      <div className="min-w-0 flex-1">
        <div className={nameClass} title={displayName}>
          {displayName}
        </div>
        {/* `title` keeps the FULL id reachable even though the row truncates it —
            the id is the thing support asks for, so it must never be lost to an
            ellipsis. */}
        <div className={idClass} title={id}>
          ID: {id}
        </div>
      </div>

      {/* Gated on an explicit prop rather than on a layout variant. Copy used to
          be "the footer one has it", which stopped meaning anything the moment
          the footer card was removed — and would have silently taken the control
          with it. */}
      {showCopy ? (
        <button
          type="button"
          onClick={handleCopy}
          className={copyBtn}
          aria-label="Organisations-ID kopieren"
          data-copy-org-id
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
          )}
        </button>
      ) : null}

      {/* Announced rather than shown: the icon swap is the visual confirmation,
          and a screen-reader user gets the same feedback without a layout shift. */}
      {showCopy ? (
        <span className="sr-only" role="status" aria-live="polite">
          {copied ? "Organisations-ID kopiert" : ""}
        </span>
      ) : null}
    </div>
  );
}
